// Loading / empty / error presentational states reused by every queue (app.md §8).
import { Body1, Button, Spinner, Subtitle2, makeStyles, tokens } from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '64px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  errorBanner: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    marginBottom: '16px',
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    marginBottom: '16px',
  },
  warningText: { flex: 1 },
})

export function Loading({ label = 'Loading…' }: { label?: string }) {
  const s = useStyles()
  return (
    <div className={s.center}>
      <Spinner label={label} />
    </div>
  )
}

/** The agreed empty text (app.md §8). */
export function EmptyQueue({ text = 'You’re all caught-up.' }: { text?: string }) {
  const s = useStyles()
  return (
    <div className={s.center}>
      <Subtitle2>{text}</Subtitle2>
    </div>
  )
}

export function ErrorBanner({ message }: { message: string | null }) {
  const s = useStyles()
  if (!message) return null
  return <div className={s.errorBanner}><Body1>{message}</Body1></div>
}

/** Dismissible warning (e.g. "couldn't refresh holds", or a filter hiding rows
    a batch action still covers). */
export function WarningBanner({ message, onDismiss }: { message: string | null; onDismiss?: () => void }) {
  const s = useStyles()
  if (!message) return null
  return (
    <div className={s.warningBanner}>
      <Body1 className={s.warningText}>{message}</Body1>
      {onDismiss && (
        <Button appearance="subtle" size="small" icon={<DismissRegular />} aria-label="Dismiss" onClick={onDismiss} />
      )}
    </div>
  )
}
